/**
 * Canonical client-side producer for `POST /api/track/listing-view`.
 *
 * Accepts INTENT (`{ listingId, guestId? }`), assembles + Zod-parses the
 * wire body internally, then fires via `navigator.sendBeacon` with a
 * `fetch(..., { keepalive: true })` fallback when `sendBeacon` is
 * unavailable (SSR, very old browser, or `sendBeacon` itself refused —
 * quota/CSP/>64KB). Fire-and-forget by design: tracking must never throw
 * into the UI, and the route always responds 204 regardless of outcome.
 *
 * Mirrors two existing patterns in this codebase rather than inventing a
 * third:
 *   - the sendBeacon-with-fetch-fallback shape of
 *     `components/providers/auth-telemetry.tsx`'s `postBeacon`.
 *   - the builder-accepts-intent / Zod-parse-before-network shape of
 *     `lib/cart-quick-checkout.ts`.
 *
 * Per CLAUDE.md Silent NACK, this is the ONE producer for this endpoint —
 * no inline `fetch`/`sendBeacon` to `/api/track/listing-view` anywhere else
 * (see tests/unit/fitness-single-producer-per-endpoint.test.ts).
 *
 * @see packages/contract/src/track-listing-view.ts — wire schema
 * @see components/analytics/listing-view-tracker.tsx — sole caller
 */

import {
  TrackListingViewRequestSchema,
  type ListingViewSurface,
  type TrackListingViewRequest,
} from "@kraahl/contract";

export interface TrackListingViewIntent {
  listingId: string;
  /** Only used server-side when there is no authenticated session. */
  guestId?: string | null;
  /**
   * Where the view happened. Omitted -> the SERVER defaults to
   * 'listing_page' (see app/api/track/listing-view/route.ts).
   */
  surface?: ListingViewSurface;
}

const TRACK_LISTING_VIEW_ENDPOINT = "/api/track/listing-view";

export function trackListingView(intent: TrackListingViewIntent): void {
  try {
    const body: TrackListingViewRequest = {
      listingId: intent.listingId,
      ...(intent.guestId ? { guestId: intent.guestId } : {}),
      ...(intent.surface ? { surface: intent.surface } : {}),
    };

    const parsed = TrackListingViewRequestSchema.safeParse(body);
    if (!parsed.success) {
      // Malformed intent (e.g. empty listingId) — never throw into the UI.
      return;
    }

    const json = JSON.stringify(parsed.data);

    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([json], { type: "application/json" });
      const queued = navigator.sendBeacon(TRACK_LISTING_VIEW_ENDPOINT, blob);
      if (queued) return;
      // sendBeacon can return false (quota / CSP / >64KB) — fall through to fetch.
    }

    void fetch(TRACK_LISTING_VIEW_ENDPOINT, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: json,
    }).catch(() => {
      // Fire-and-forget — a dropped tracking beacon is not a user-facing error.
    });
  } catch {
    // Tracking must never throw into the UI.
  }
}
